/**
 * Offline-first FEMA Cost Entry Service
 *
 * Handles creating, storing, and syncing FEMA cost entries when offline.
 * Uses IndexedDB for local storage and syncs to the server when online.
 */

import { db, CachedFemaCost } from './db';
import { mustDeferSend, sendTenantOf } from './client-id';
import { parseApiError } from '@/utils/api-errors';
import { api } from '@/services/api';
import { useTenantStore } from '@/store/tenant.store';

/**
 * Server-side update support per cost type. Only /labor/:id and
 * /equipment/:id have PATCH routes — materials and contracts are
 * create-only on the server, so edits to already-synced entries of those
 * types cannot be replayed (a prior version queued them and they 404'd
 * forever).
 */
const PATCHABLE_COST_TYPES = new Set<CachedFemaCost['cost_type']>(['personnel', 'equipment']);

/**
 * Server-side hard-delete support per cost type. Only /labor/:id and
 * /equipment/:id expose a DELETE route; materials and contracts have none,
 * so an offline delete of an already-synced row of those types cannot be
 * replayed — we surface it as an error instead of pretending it synced.
 */
const DELETABLE_COST_TYPES = new Set<CachedFemaCost['cost_type']>(['personnel', 'equipment']);

/**
 * Read the currently-active tenant id (or null if none / SSR).
 */
function getActiveTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}

function getErrorStatus(err: any): number | undefined {
  return err?.response?.status;
}

function getErrorCode(err: any): string | undefined {
  return err?.response?.data?.error?.code ?? err?.response?.data?.code;
}

class FemaCostOfflineService {
  async create(tenantId: string, incidentId: string, data: Partial<CachedFemaCost>): Promise<CachedFemaCost> {
    const offlineId = `offline_${crypto.randomUUID()}`;
    const now = new Date().toISOString();
    const entry: CachedFemaCost = {
      id: offlineId,
      offline_id: offlineId,
      tenant_id: tenantId,
      incident_id: incidentId,
      cost_type: data.cost_type || 'personnel',
      is_synced: false,
      pending_changes: true,
      created_at: now,
      updated_at: now,
      ...data,
    };
    await db.cachedFemaCosts.put(entry);
    return entry;
  }

  async update(costId: string, data: Partial<CachedFemaCost>): Promise<void> {
    // Refuse to queue edits that can never sync: materials/contracts entries
    // that already exist server-side have no PATCH endpoint. (Entries created
    // offline and not yet synced are fine — they sync via POST.)
    const existing = await db.cachedFemaCosts.get(costId);
    if (existing && existing.id !== existing.offline_id && !PATCHABLE_COST_TYPES.has(existing.cost_type)) {
      throw new Error(
        `Offline edits to ${existing.cost_type} entries are not supported — the server has no update endpoint for this cost type. Edit it while online by recreating the entry.`
      );
    }
    await db.cachedFemaCosts.update(costId, {
      ...data,
      pending_changes: true,
      is_synced: false,
      updated_at: new Date().toISOString(),
    });
  }

  async delete(costId: string): Promise<void> {
    const entry = await db.cachedFemaCosts.get(costId);
    if (entry && entry.id === entry.offline_id) {
      // Never synced, just delete locally
      await db.cachedFemaCosts.delete(costId);
    } else if (entry) {
      // Server-backed entry: mark a deletion tombstone so syncCost issues a
      // server DELETE. Without pending_delete the prior version only flipped
      // pending_changes/is_synced and syncCost would (wrongly) PATCH it, so the
      // row never left the server and could resurrect on cacheFromServer.
      await db.cachedFemaCosts.update(costId, {
        pending_delete: true,
        pending_changes: true,
        is_synced: false,
        updated_at: new Date().toISOString(),
      });
    }
  }

  async getById(costId: string): Promise<CachedFemaCost | undefined> {
    const entry = await db.cachedFemaCosts.get(costId);
    if (entry) return entry;
    return db.cachedFemaCosts.where('offline_id').equals(costId).first();
  }

  async list(tenantId: string, incidentId: string): Promise<CachedFemaCost[]> {
    return db.cachedFemaCosts
      .where('tenant_id')
      .equals(tenantId)
      .filter(c => c.incident_id === incidentId)
      .toArray();
  }

  async getPendingCount(tenantId: string): Promise<number> {
    return db.cachedFemaCosts
      // Loose check: is_synced is written as boolean false here, and IndexedDB
      // can't index booleans — an index .equals(0) silently misses those rows.
      .filter(c => !c.is_synced && c.tenant_id === tenantId)
      .count();
  }

  async syncCost(costId: string): Promise<void> {
    const entry = await db.cachedFemaCosts.get(costId);
    if (!entry || entry.is_synced) return;
    // Contract 9 gate (client-id.ts mustDeferSend): never sent as someone else.
    if (mustDeferSend(entry)) return;

    // Map cost_type to endpoint
    const endpointMap: Record<string, string> = {
      personnel: '/fema-reimbursement/labor',
      equipment: '/fema-reimbursement/equipment',
      supplies: '/fema-reimbursement/materials',
      contracts: '/fema-reimbursement/contracts',
    };
    const endpoint = endpointMap[entry.cost_type];
    if (!endpoint) throw new Error(`Unknown cost type: ${entry.cost_type}`);

    // Deletion tombstone: replay the server DELETE before any create/edit path.
    if (entry.pending_delete) {
      // Offline-only rows never reached the server — drop locally. (delete()
      // already hard-deletes these, but guard defensively.)
      if (entry.id === entry.offline_id) {
        await db.cachedFemaCosts.delete(entry.id);
        return;
      }

      // Materials/contracts have no DELETE route — the delete can never be
      // replayed. Surface it and leave the row pending rather than silently
      // marking it synced (which would hide a delete the user requested).
      if (!DELETABLE_COST_TYPES.has(entry.cost_type)) {
        await db.cachedFemaCosts.update(entry.id, {
          last_error: `Offline deletion of ${entry.cost_type} entries cannot be synced (the server has no delete endpoint for this cost type). Remove it while online.`,
        });
        throw new Error(
          `No server delete endpoint for ${entry.cost_type} entries`
        );
      }

      try {
        await api.delete(`${endpoint}/${entry.id}`, undefined, sendTenantOf(entry));
        await db.cachedFemaCosts.delete(entry.id);
      } catch (err: any) {
        // Already gone on the server — treat as deleted locally too.
        if (getErrorStatus(err) === 404) {
          await db.cachedFemaCosts.delete(entry.id);
          return;
        }
        const errorMsg =
          err?.response?.data?.error?.message ||
          parseApiError(err).message;
        await db.cachedFemaCosts.update(entry.id, { last_error: errorMsg });
        throw err;
      }
      return;
    }

    try {
      const isNew = entry.id === entry.offline_id;
      if (isNew) {
        const response = await api.post<{ data: any }>(endpoint, {
          incident_id: entry.incident_id,
          pa_category: entry.pa_category,
          description: entry.description,
          amount: entry.amount || entry.total_cost,
          // Labor is timesheet-first: the create schema's description field
          // is activity_description (plain `description` is stripped).
          ...(entry.cost_type === 'personnel'
            ? {
                activity_description: entry.description,
                ...(entry.worked_date ? { worked_date: entry.worked_date } : {}),
              }
            : {}),
          ...entry.details,
        }, sendTenantOf(entry));
        const payload = response.data;

        // 202 path (labor + equipment): the entry became a canonical
        // timesheet / equipment_usage awaiting Time Unit / Equipment Unit
        // approval — no FEMA lens row exists yet. The server now owns the
        // record (it appears in the relevant approval queue and will be cached
        // here after approval), so drop the local offline copy.
        if (payload?.pending_approval) {
          await db.cachedFemaCosts.delete(entry.id);
          return;
        }

        // Replace offline ID with server ID
        const serverId = payload?.id || payload?.incident_cost_id || entry.id;
        await db.cachedFemaCosts.delete(entry.id);
        await db.cachedFemaCosts.put({
          ...entry,
          id: serverId,
          is_synced: true,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          last_error: undefined,
        });
      } else {
        // Edits of already-synced entries. Materials/contracts have no PATCH
        // route — a legacy queued edit can never be applied; surface it once
        // instead of retrying forever (server state wins on the next cache).
        if (!PATCHABLE_COST_TYPES.has(entry.cost_type)) {
          await db.cachedFemaCosts.update(entry.id, {
            is_synced: true,
            pending_changes: false,
            synced_at: new Date().toISOString(),
            last_error: `Offline edits to ${entry.cost_type} entries cannot be synced (no server update endpoint); the server version was kept.`,
          });
          return;
        }

        // Labor & equipment lens rows: only FEMA-lens metadata is editable
        // offline. Sending the cached hour/quantity/rate snapshot back would
        // trigger a privileged timesheet / equipment_usage edit (or a 400 on
        // grandfathered rows), so the PATCH body is metadata-only
        // (pa_category + activity_description). Materials/contracts (not
        // reachable here — gated above) keep the full-details shape.
        const patchBody =
          entry.cost_type === 'personnel' || entry.cost_type === 'equipment'
            ? {
                pa_category: entry.pa_category,
                activity_description: entry.description,
              }
            : {
                ...entry.details,
                pa_category: entry.pa_category,
                description: entry.description,
              };
        await api.patch(`${endpoint}/${entry.id}`, patchBody, sendTenantOf(entry));
        await db.cachedFemaCosts.update(entry.id, {
          is_synced: true,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          last_error: undefined,
        });
      }
    } catch (err: any) {
      // Permanent rejections (defensive): pre-consolidation labor rows reject
      // hour/rate edits with 400 GRANDFATHERED_LABOR_READONLY. Stop retrying
      // and keep the server version; the message stays visible on the entry.
      if (getErrorStatus(err) === 400 && getErrorCode(err) === 'GRANDFATHERED_LABOR_READONLY') {
        await db.cachedFemaCosts.update(entry.id, {
          is_synced: true,
          pending_changes: false,
          synced_at: new Date().toISOString(),
          last_error:
            'This labor entry predates the timesheet consolidation — hour and rate edits must be made as new Time Unit timesheet entries. The server version was kept.',
        });
        return;
      }
      if (getErrorStatus(err) === 400 && getErrorCode(err) === 'GRANDFATHERED_EQUIPMENT_READONLY') {
        await db.cachedFemaCosts.update(entry.id, {
          is_synced: true,
          pending_changes: false,
          synced_at: new Date().toISOString(),
          last_error:
            'This equipment entry predates the equipment-usage consolidation — quantity and rate edits must be made as new Equipment Unit usage entries. The server version was kept.',
        });
        return;
      }
      const errorMsg = err?.response?.data?.error?.message || parseApiError(err).message;
      await db.cachedFemaCosts.update(entry.id, { last_error: errorMsg });
      throw err;
    }
  }

  async syncAll(): Promise<{ synced: number; failed: number; errors: string[] }> {
    // Loose check (not .where('is_synced').equals(0)): rows are written with a
    // boolean is_synced, which IndexedDB can't index, so the index query missed
    // them and offline FEMA costs never synced.
    const pending = await db.cachedFemaCosts.filter(c => !c.is_synced).toArray();
    const activeTenantId = getActiveTenantId();
    let synced = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const entry of pending) {
      // Never sent as someone else (S-06): another originator's row waits for them, or for the handoff.
      if (mustDeferSend(entry as never)) continue;
      // Tenant guard: never replay a record against a different tenant than the
      // one it was created under (the api client injects the ambient X-Tenant-ID).
      // Defer (leave pending) — it will sync when its own tenant is active again.
      if (entry.tenant_id != null && activeTenantId != null && entry.tenant_id !== activeTenantId) {
        continue;
      }
      try {
        await this.syncCost(entry.id);
        synced++;
      } catch (err: any) {
        failed++;
        errors.push(`${entry.id}: ${err.message}`);
      }
    }
    return { synced, failed, errors };
  }

  async cacheFromServer(tenantId: string, incidentId: string): Promise<void> {
    try {
      // api.get takes the params object directly (it is not an axios config);
      // the FEMA cost list endpoints cap `limit` at 100.
      const [labor, equipment, materials, contracts] = await Promise.all([
        api.get<{ data: any[] }>('/fema-reimbursement/labor', { incident_id: incidentId, limit: 100 }),
        api.get<{ data: any[] }>('/fema-reimbursement/equipment', { incident_id: incidentId, limit: 100 }),
        api.get<{ data: any[] }>('/fema-reimbursement/materials', { incident_id: incidentId, limit: 100 }),
        api.get<{ data: any[] }>('/fema-reimbursement/contracts', { incident_id: incidentId, limit: 100 }),
      ]);

      const typeMap: Record<string, CachedFemaCost['cost_type']> = {
        labor: 'personnel',
        equipment: 'equipment',
        materials: 'supplies',
        contracts: 'contracts',
      };

      const allResults = [
        { data: labor.data || [], type: 'labor' },
        { data: equipment.data || [], type: 'equipment' },
        { data: materials.data || [], type: 'materials' },
        { data: contracts.data || [], type: 'contracts' },
      ];

      for (const { data: records, type } of allResults) {
        const items = Array.isArray(records) ? records : [];
        for (const item of items) {
          const existing = await db.cachedFemaCosts.get(item.id);
          if (existing?.pending_delete) {
            // Deleted offline, server DELETE still pending — never resurrect
            // the row from the server snapshot. Leave the tombstone in place
            // so syncCost can replay the DELETE.
            continue;
          }
          if (existing?.pending_changes) {
            // Local has unsaved changes — record conflict for UI resolution
            await db.cachedFemaCosts.update(existing.id, {
              conflict_state: 'detected',
              server_updated_at: item.updated_at,
              server_snapshot: item,
            });
            continue;
          }
          const mapped: CachedFemaCost = {
            id: item.id,
            offline_id: item.offline_id || item.id,
            tenant_id: tenantId,
            incident_id: incidentId,
            cost_type: typeMap[type],
            pa_category: item.pa_category,
            amount: item.amount,
            description: item.description,
            worked_date: item.worked_date,
            used_date: item.used_date,
            hours: item.hours,
            rate_used: item.rate_used,
            total_cost: item.total_cost,
            personnel_name: item.personnel_name,
            equipment_name: item.equipment_name,
            vendor_name: item.vendor_name,
            is_synced: true,
            synced_at: new Date().toISOString(),
            pending_changes: false,
            server_updated_at: item.updated_at,
            created_at: item.created_at || new Date().toISOString(),
            updated_at: item.updated_at || new Date().toISOString(),
            details: item,
          };
          await db.cachedFemaCosts.put(mapped);
        }
      }
    } catch {
      // Offline — skip server cache
    }
  }

  async clearCache(): Promise<void> {
    await db.cachedFemaCosts.clear();
  }

  async clearSyncedCache(): Promise<void> {
    await db.cachedFemaCosts
      .filter(c => c.is_synced && !c.pending_changes)
      .delete();
  }
}

export const femaCostOfflineService = new FemaCostOfflineService();
